import { useState, useEffect } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { fmt$, fmtN, fmtPct, parseOCC, ACTION_COLORS } from "../lib/format";
import { fastGetAsync } from "../lib/fastFetch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Omega AI Watch Room — Active Monitoring of ALL Positions ──────────────────
function OmegaWatchRoom({ onQuickTrade, onTrailStop }) {
  const [watchData, setWatchData] = useState(null);
  const [trailStops, setTrailStops] = useState([]);

  useEffect(() => {
    const load = () => fastGetAsync(`${API}/omega/watch`).then(r => setWatchData(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  // Fetch trail stops for armed chips
  useEffect(() => {
    const loadTrailStops = () => axios.get(`${API}/trail-stops?status=armed`).then(r => setTrailStops(r.data?.stops || [])).catch(() => {});
    loadTrailStops();
    const iv = setInterval(loadTrailStops, 8000);
    return () => clearInterval(iv);
  }, []);

  // Build symbol -> trail stop map for quick lookup
  const trailBySymbol = {};
  trailStops.forEach(ts => { trailBySymbol[ts.symbol] = ts; });

  if (!watchData || !watchData.positions?.length) {
    return (
      <div className="card" style={{ borderColor: "rgba(167,139,250,0.2)" }}>
        <div className="sh">Omega AI Watch Room</div>
        <div style={{ textAlign: "center", padding: 16, color: "var(--dim)", fontSize: 12 }}>
          <span className="spin" style={{ marginRight: 8 }} /> Loading position monitoring...
        </div>
      </div>
    );
  }

  const actionColors = ACTION_COLORS;

  return (
    <div className="card" style={{ borderColor: "rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.02)" }} data-testid="omega-watch-room">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div className="sh" style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
          Omega AI Watch Room
          <span className="tag t-pur" style={{ fontSize: 8 }}>LIVE MONITORING</span>
        </div>
        <span style={{ fontSize: 9, color: "var(--dim)", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", display: "inline-block", animation: "pulse 2s infinite" }} />
          {watchData.count} positions tracked
        </span>
      </div>

      {watchData.positions.map((w, i) => (
        <div key={i} style={{
          background: "var(--layer2)", borderRadius: 10, padding: 14, marginBottom: 8,
          borderLeft: `3px solid ${actionColors[w.action] || "#60a5fa"}`,
        }} data-testid={`watch-item-${i}`}>
          {/* Header Row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{w.ticker}</span>
              {w.type === "OPTION" && (
                <>
                  <span style={{ fontSize: 12, color: "#60a5fa", marginLeft: 6, fontWeight: 500 }}>${w.strike} {w.side}</span>
                  <span style={{ fontSize: 9, color: "var(--dim)", marginLeft: 4 }}>Exp {w.expDate}</span>
                  <span className={`tag ${w.itm ? "t-bull" : "t-bear"}`} style={{ fontSize: 7, marginLeft: 6 }}>{w.itm ? "ITM" : "OTM"}</span>
                  <span className="tag t-pur" style={{ fontSize: 7, marginLeft: 4 }}>{w.dte}d</span>
                </>
              )}
              {w.type === "CRYPTO" && <span style={{ fontSize: 10, color: "var(--muted)", marginLeft: 6 }}>{w.display}</span>}
              {w.teaScore != null && (
                <span
                  data-testid={`watch-tea-${w.ticker}`}
                  title={`Tea Score · ${w.teaSentiment || "neutral"}`}
                  style={{
                    fontSize: 8, marginLeft: 6, padding: "2px 6px", borderRadius: 4, fontWeight: 700,
                    background: w.teaScore >= 70 ? "rgba(74,222,128,0.15)" : w.teaScore >= 40 ? "rgba(250,204,21,0.15)" : "rgba(239,68,68,0.15)",
                    color: w.teaScore >= 70 ? "#4ade80" : w.teaScore >= 40 ? "#fbbf24" : "#f87171",
                  }}
                >TEA {w.teaScore}</span>
              )}
              {trailBySymbol[w.symbol] && (
                <span
                  data-testid={`trail-armed-${w.ticker}`}
                  title={`Trail stop armed at ${((trailBySymbol[w.symbol].trailPct || 0) * 100).toFixed(0)}% below peak $${Number(trailBySymbol[w.symbol].peakPrice || 0).toFixed(2)}`}
                  style={{
                    fontSize: 8, marginLeft: 6, padding: "2px 6px", borderRadius: 4, fontWeight: 700,
                    background: "rgba(74,222,128,0.15)", color: "#4ade80",
                    display: "inline-flex", alignItems: "center", gap: 3,
                  }}
                >
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#4ade80", animation: "pulse 2s infinite" }} />
                  TRAIL {((trailBySymbol[w.symbol].trailPct || 0) * 100).toFixed(0)}%
                </span>
              )}
            </div>
            <div style={{
              padding: "3px 10px", borderRadius: 6, fontSize: 10, fontWeight: 700,
              background: `${actionColors[w.action] || "#60a5fa"}15`,
              color: actionColors[w.action] || "#60a5fa",
              border: `1px solid ${actionColors[w.action] || "#60a5fa"}30`,
            }}>{w.action}</div>
          </div>

          {/* Metrics Row — Options */}
          {w.type === "OPTION" && (<>
            <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
              <div className="m-box" style={{ flex: 1, minWidth: 70, padding: "5px 8px" }}>
                <div className="m-lbl" style={{ fontSize: 7 }}>Stock Price</div>
                <div className="m-val" style={{ fontSize: 13 }}>${fmtN(w.stockPrice, 2)}</div>
              </div>
              <div className="m-box" style={{ flex: 1, minWidth: 70, padding: "5px 8px" }}>
                <div className="m-lbl" style={{ fontSize: 7 }}>Bid / Ask</div>
                <div className="m-val" style={{ fontSize: 12 }}>${fmtN(w.optionBid, 2)} / ${fmtN(w.optionAsk, 2)}</div>
              </div>
              <div className="m-box" style={{ flex: 1, minWidth: 70, padding: "5px 8px" }}>
                <div className="m-lbl" style={{ fontSize: 7 }}>Moneyness</div>
                <div className={`m-val ${w.itm ? "pos" : "neg"}`} style={{ fontSize: 13 }}>
                  {w.itm ? "ITM" : "OTM"} {Math.abs(w.moneyness)}%
                </div>
              </div>
              <div className="m-box" style={{ flex: 1, minWidth: 70, padding: "5px 8px" }}>
                <div className="m-lbl" style={{ fontSize: 7 }}>Unrealized</div>
                <div className={`m-val ${w.gainPct >= 0 ? "pos" : "neg"}`} style={{ fontSize: 13 }}>
                  {w.gainPct >= 0 ? "+" : ""}{fmtN(w.gainPct, 1)}%
                </div>
              </div>
              <div className="m-box" style={{ flex: 1, minWidth: 70, padding: "5px 8px" }}>
                <div className="m-lbl" style={{ fontSize: 7 }}>Breakeven</div>
                <div className="m-val" style={{ fontSize: 12 }}>${fmtN(w.breakeven, 2)}</div>
                <div style={{ fontSize: 8, color: "var(--dim)" }}>+{fmtN(w.distToBreakeven, 1)}% away</div>
              </div>
            </div>
            {/* Greeks Row */}
            {w.greeks && Object.keys(w.greeks).length > 0 && (
              <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                {w.greeks.delta != null && <div className="m-box" style={{ flex: 1, minWidth: 55, padding: "4px 6px" }}>
                  <div className="m-lbl" style={{ fontSize: 7 }}>Delta</div>
                  <div className="m-val" style={{ fontSize: 12, color: "#60a5fa" }}>{fmtN(w.greeks.delta, 3)}</div>
                </div>}
                {w.greeks.gamma != null && <div className="m-box" style={{ flex: 1, minWidth: 55, padding: "4px 6px" }}>
                  <div className="m-lbl" style={{ fontSize: 7 }}>Gamma</div>
                  <div className="m-val" style={{ fontSize: 12 }}>{fmtN(w.greeks.gamma, 3)}</div>
                </div>}
                {w.greeks.theta != null && <div className="m-box" style={{ flex: 1, minWidth: 55, padding: "4px 6px" }}>
                  <div className="m-lbl" style={{ fontSize: 7 }}>Theta</div>
                  <div className="m-val" style={{ fontSize: 12, color: "#f87171" }}>{fmtN(w.greeks.theta, 4)}</div>
                </div>}
                {w.greeks.vega != null && <div className="m-box" style={{ flex: 1, minWidth: 55, padding: "4px 6px" }}>
                  <div className="m-lbl" style={{ fontSize: 7 }}>Vega</div>
                  <div className="m-val" style={{ fontSize: 12 }}>{fmtN(w.greeks.vega, 4)}</div>
                </div>}
                {w.greeks.iv != null && <div className="m-box" style={{ flex: 1, minWidth: 55, padding: "4px 6px" }}>
                  <div className="m-lbl" style={{ fontSize: 7 }}>IV</div>
                  <div className="m-val" style={{ fontSize: 12, color: "#fbbf24" }}>{fmtN(w.greeks.iv * 100, 0)}%</div>
                </div>}
              </div>
            )}
            {/* Cost basis row */}
            <div style={{ display: "flex", gap: 10, fontSize: 9, color: "var(--dim)", marginBottom: 6, padding: "2px 4px" }}>
              <span>Qty: {w.quantity} contracts</span>
              {w.avgCost != null && <span>Avg Cost: ${fmtN(w.avgCost, 4)}</span>}
              {w.totalCost != null && <span>Cost Basis: {fmt$(w.totalCost)}</span>}
              <span>Value: {fmt$(w.currentValue)}</span>
              <span>Vol: {w.optionVolume ? Number(w.optionVolume).toLocaleString() : "—"}</span>
            </div>
          </>)}

          {/* Metrics Row — Crypto */}
          {w.type === "CRYPTO" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <div className="m-box" style={{ flex: 1, minWidth: 80, padding: "6px 8px" }}>
                <div className="m-lbl" style={{ fontSize: 8 }}>Price</div>
                <div className="m-val" style={{ fontSize: 13 }}>${fmtN(w.lastPrice, 4)}</div>
              </div>
              <div className="m-box" style={{ flex: 1, minWidth: 80, padding: "6px 8px" }}>
                <div className="m-lbl" style={{ fontSize: 8 }}>Today</div>
                <div className={`m-val ${w.dayGainPct >= 0 ? "pos" : "neg"}`} style={{ fontSize: 13 }}>
                  {w.dayGainPct >= 0 ? "+" : ""}{fmtN(w.dayGainPct, 1)}%
                </div>
              </div>
              <div className="m-box" style={{ flex: 1, minWidth: 80, padding: "6px 8px" }}>
                <div className="m-lbl" style={{ fontSize: 8 }}>Value</div>
                <div className="m-val" style={{ fontSize: 13 }}>{fmt$(w.currentValue)}</div>
              </div>
            </div>
          )}

          {/* AI Signals */}
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {w.signals.map((s, j) => (
              <div key={j} style={{ fontSize: 10, color: "var(--muted)", display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}>
                <span style={{ width: 4, height: 4, borderRadius: "50%", background: s.includes("URGENT") || s.includes("Sharp") ? "#f87171" : s.includes("Surging") || s.includes("ITM") || s.includes("bullish") ? "#4ade80" : "#a78bfa", flexShrink: 0 }} />
                {s}
              </div>
            ))}
          </div>

          {/* Quick-trade buttons */}
          {onQuickTrade && (
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <button
                data-testid={`watch-sell-${w.ticker}`}
                onClick={() => onQuickTrade({ symbol: w.symbol, ticker: w.ticker, quantity: w.quantity, lastPrice: w.optionPrice || w.lastPrice, avgCost: w.avgCost, type: w.type }, "sell")}
                style={{ flex: 1, padding: "6px 8px", borderRadius: 6, background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.28)", color: "#f87171", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
              >Sell</button>
              <button
                data-testid={`watch-buy-more-${w.ticker}`}
                onClick={() => onQuickTrade({ symbol: w.symbol, ticker: w.ticker, quantity: w.quantity, lastPrice: w.optionPrice || w.lastPrice, avgCost: w.avgCost, type: w.type }, "buy_more")}
                style={{ flex: 1, padding: "6px 8px", borderRadius: 6, background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.28)", color: "#4ade80", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
              >Buy More</button>
              {onTrailStop && (
                <button
                  data-testid={`watch-trail-${w.ticker}`}
                  onClick={() => onTrailStop({ symbol: w.symbol, ticker: w.ticker, quantity: w.quantity, lastPrice: w.optionPrice || w.lastPrice, avgCost: w.avgCost, type: w.type })}
                  title="Arm trailing stop"
                  style={{ flex: 1, padding: "6px 8px", borderRadius: 6, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.28)", color: "#fbbf24", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                >Trail</button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Live Position Card (Dashboard) ───────────────────────────────────────────
function LivePositionCard({ p, onClick, onQuickTrade, onTrailStop }) {
  const [miniChart, setMiniChart] = useState(null);
  const [livePnl, setLivePnl] = useState({ dayGain: p.dayGainValue || 0, dayGainPct: p.dayGainPct || 0 });
  const isOption = String(p.type || "").includes("OPTION");
  const isCrypto = String(p.type || "").includes("CRYPTO");
  const occ = isOption ? parseOCC(p.symbol) : null;
  const displayTicker = occ ? occ.ticker : p.symbol;
  const chartSymbol = isCrypto ? `${p.symbol}-USD` : (occ ? occ.ticker : p.symbol);
  const gainPct = p.gainPct || 0;

  // Update live P&L from parent refresh
  useEffect(() => {
    setLivePnl({ dayGain: p.dayGainValue || 0, dayGainPct: p.dayGainPct || 0 });
  }, [p.dayGainValue, p.dayGainPct]);

  // Fetch intraday chart on mount + refresh every 30s
  useEffect(() => {
    const buildMini = (bars) => {
      const closes = bars.map(b => b.c).filter(v => v != null);
      if (closes.length < 2) return null;
      const up = closes[closes.length - 1] >= closes[0];
      return {
        labels: bars.map(b => { const ms = b.t > 1e12 ? b.t : b.t * 1000; return new Date(ms).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }); }),
        datasets: [{ data: closes, borderColor: up ? "#4ade80" : "#f87171", backgroundColor: up ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.2 }],
      };
    };

    const load = async () => {
      // 1. Nasdaq via /market/chart (most reliable — no Yahoo rate limits)
      const sym = occ ? occ.ticker : p.symbol;
      try {
        const r = await axios.get(`${API}/market/chart`, { params: { symbol: sym, range: "1D" } });
        const bars = r.data?.bars || [];
        const chart = buildMini(bars);
        if (chart) { setMiniChart(chart); return; }
      } catch {}
      // 2. Live tick accumulator fallback
      try {
        const r = await axios.get(`${API}/ticks/history`, { params: { symbol: sym } });
        const bars = r.data?.bars || [];
        const chart = buildMini(bars);
        if (chart) setMiniChart(chart);
      } catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSymbol]);

  const miniOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } }, elements: { line: { borderWidth: 1.5 } } };

  return (
    <div className="pos-card" style={{ cursor: "pointer", transition: "background 0.15s", padding: 14 }}
      onClick={() => onClick({ ...p, ticker: displayTicker, source: "portfolio" })}
      onMouseOver={e => e.currentTarget.style.background = "rgba(56,189,248,0.04)"}
      onMouseOut={e => e.currentTarget.style.background = ""}
      data-testid={`position-card-${p.symbol}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{displayTicker}</span>
          {occ && <span style={{ fontSize: 11, color: "#60a5fa", marginLeft: 6, fontWeight: 500 }}>${occ.strike} {occ.side}</span>}
          {occ && <span style={{ fontSize: 9, color: "var(--dim)", marginLeft: 4 }}>Exp {occ.expDate}</span>}
          {!occ && <span style={{ fontSize: 10, color: "var(--muted)", marginLeft: 6 }}>{p.name}</span>}
          <span className={`tag ${isCrypto ? "t-neu" : isOption ? "t-bull" : "t-blue"}`} style={{ marginLeft: 6, fontSize: 7 }}>{isOption ? "OPTION" : isCrypto ? "CRYPTO" : "STOCK"}</span>
          {p.teaScore != null && (
            <span style={{ fontSize: 8, marginLeft: 6, padding: "1px 5px", borderRadius: 4, fontWeight: 700,
              background: p.teaScore >= 70 ? "rgba(74,222,128,0.15)" : p.teaScore >= 40 ? "rgba(250,204,21,0.15)" : "rgba(239,68,68,0.15)",
              color: p.teaScore >= 70 ? "#4ade80" : p.teaScore >= 40 ? "#fbbf24" : "#f87171",
            }}>TEA {p.teaScore}</span>
          )}
          {p.trailStop && (
            <span style={{ fontSize: 8, marginLeft: 6, padding: "1px 5px", borderRadius: 4, fontWeight: 700, background: "rgba(74,222,128,0.15)", color: "#4ade80", display: "inline-flex", alignItems: "center", gap: 3 }}>
              <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#4ade80", animation: "pulse 2s infinite" }} />TRAIL
            </span>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{fmt$(p.currentValue)}</div>
          <div className={gainPct >= 0 ? "pos" : "neg"} style={{ fontSize: 12, fontWeight: 600 }}>{gainPct >= 0 ? "+" : ""}{fmtPct(gainPct)} ({gainPct >= 0 ? "+" : ""}{fmt$(p.gainValue)})</div>
        </div>
      </div>

      {/* Live Intraday Mini Chart */}
      {miniChart && (
        <div style={{ height: 48, marginBottom: 6, borderRadius: 6, overflow: "hidden", background: "rgba(0,0,0,0.15)" }}>
          <Line data={miniChart} options={miniOpts} />
        </div>
      )}

      {/* Greeks row — options only */}
      {isOption && p.greeks && Object.keys(p.greeks).length > 0 && (
        <div style={{ display: "flex", gap: 5, marginBottom: 6, flexWrap: "wrap" }}>
          {p.greeks.delta != null && <div style={{ padding: "3px 7px", borderRadius: 5, background: "var(--layer2)", border: "1px solid var(--border)", fontSize: 9 }}><span style={{ color: "var(--muted)" }}>Δ </span><span style={{ color: "#60a5fa", fontWeight: 700 }}>{fmtN(p.greeks.delta, 3)}</span></div>}
          {p.greeks.gamma != null && <div style={{ padding: "3px 7px", borderRadius: 5, background: "var(--layer2)", border: "1px solid var(--border)", fontSize: 9 }}><span style={{ color: "var(--muted)" }}>Γ </span><span style={{ fontWeight: 700 }}>{fmtN(p.greeks.gamma, 3)}</span></div>}
          {p.greeks.theta != null && <div style={{ padding: "3px 7px", borderRadius: 5, background: "var(--layer2)", border: "1px solid var(--border)", fontSize: 9 }}><span style={{ color: "var(--muted)" }}>Θ </span><span style={{ color: "#f87171", fontWeight: 700 }}>${fmtN(Math.abs(p.greeks.theta || 0) * 100, 2)}/d</span></div>}
          {p.greeks.vega != null && <div style={{ padding: "3px 7px", borderRadius: 5, background: "var(--layer2)", border: "1px solid var(--border)", fontSize: 9 }}><span style={{ color: "var(--muted)" }}>V </span><span style={{ fontWeight: 700 }}>{fmtN(p.greeks.vega, 3)}</span></div>}
          {p.greeks.iv != null && <div style={{ padding: "3px 7px", borderRadius: 5, background: "var(--layer2)", border: "1px solid var(--border)", fontSize: 9 }}><span style={{ color: "var(--muted)" }}>IV </span><span style={{ color: "#fbbf24", fontWeight: 700 }}>{fmtN((p.greeks.iv || 0) * 100, 0)}%</span></div>}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, fontSize: 10, color: "var(--muted)", marginBottom: 6, flexWrap: "wrap" }}>
        <span>Qty: {isOption ? p.quantity + " contract" + (p.quantity > 1 ? "s" : "") : fmtN(p.quantity, 2)}</span>
        <span>Last: ${fmtN(p.lastPrice, 4)}</span>
        {p.avgCost != null && <span>Avg Cost: ${fmtN(p.avgCost, 4)}</span>}
        {p.totalCost != null && <span>Cost Basis: {fmt$(p.totalCost)}</span>}
        <span>% of Portfolio: {fmtPct(p.pctOfPortfolio)}</span>
      </div>

      {/* Day P&L — live updating */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", padding: "6px 10px", borderRadius: 6, background: livePnl.dayGain >= 0 ? "rgba(74,222,128,0.06)" : "rgba(248,113,113,0.06)", fontSize: 10, border: livePnl.dayGain >= 0 ? "1px solid rgba(74,222,128,0.15)" : "1px solid rgba(248,113,113,0.15)" }}>
        <span style={{ color: "var(--muted)", fontWeight: 500 }}>Today:</span>
        <span className={livePnl.dayGain >= 0 ? "pos" : "neg"} style={{ fontWeight: 700, fontSize: 12 }}>{livePnl.dayGain >= 0 ? "+" : ""}{fmt$(livePnl.dayGain)}</span>
        <span className={livePnl.dayGainPct >= 0 ? "pos" : "neg"} style={{ fontWeight: 600 }}>({livePnl.dayGainPct >= 0 ? "+" : ""}{fmtN(livePnl.dayGainPct, 1)}%)</span>
        <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#4ade80", display: "inline-block", animation: "pulse 2s infinite" }} />
        {isOption && <span style={{ color: livePnl.dayGainPct > 20 ? "#4ade80" : livePnl.dayGainPct < -15 ? "#f87171" : "#a78bfa", marginLeft: "auto", fontWeight: 500 }}>Omega AI: {
          livePnl.dayGainPct > 30 ? "Surging — consider scaling out to lock gains" :
          livePnl.dayGainPct > 15 ? "Strong momentum — trail your stop" :
          livePnl.dayGainPct < -25 ? "Sharp drop — evaluate exit" :
          livePnl.dayGainPct < -10 ? "Weak today — tighten risk" :
          gainPct > 20 ? "Profitable — consider taking profits" :
          gainPct < -30 ? "Deep drawdown — review position" :
          "Hold — within target range"
        }</span>}
        {isCrypto && <span style={{ color: "#facc15", marginLeft: "auto" }}>Crypto — volatile asset</span>}
      </div>

      {/* Quick-trade buttons — Sell / Buy More / Trail Stop */}
      {onQuickTrade && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }} onClick={e => e.stopPropagation()}>
          <button
            data-testid={`pos-sell-${p.symbol}`}
            onClick={(e) => { e.stopPropagation(); onQuickTrade("sell"); }}
            style={{ flex: 1, padding: "8px 8px", borderRadius: 8, background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.28)", color: "#f87171", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Sell
          </button>
          <button
            data-testid={`pos-buy-more-${p.symbol}`}
            onClick={(e) => { e.stopPropagation(); onQuickTrade("buy_more"); }}
            style={{ flex: 1, padding: "8px 8px", borderRadius: 8, background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.28)", color: "#4ade80", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Buy More
          </button>
          {onTrailStop && (
            <button
              data-testid={`pos-trail-${p.symbol}`}
              onClick={(e) => { e.stopPropagation(); onTrailStop(); }}
              title="Arm a trailing stop to auto-sell if price drops X% from peak"
              style={{ flex: 1, padding: "8px 8px", borderRadius: 8, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.28)", color: "#fbbf24", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              Trail
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export { OmegaWatchRoom, LivePositionCard };
